// Tjänstekatalogen: byråns tjänster, hur varje tjänst prissätts och byråns
// gränser för automatiska offerter. Se "Prismotor och tjänstekatalog" i
// docs/ARCHITECTURE.md. Alla belopp är heltal i öre exklusive moms (ADR 0014).
import { z } from 'zod';

/** Ett belopp i öre exklusive moms. 1 500 kr skrivs 150000. */
export const oreSchema = z.int().nonnegative();
export type Ore = z.infer<typeof oreSchema>;

/**
 * Bolagsformer som en tjänst kan prissättas eller begränsas efter: aktiebolag,
 * enskild firma, handelsbolag, kommanditbolag, ekonomisk förening,
 * bostadsrättsförening och ideell förening (ADR 0101).
 */
export const legalFormSchema = z.enum(['AB', 'EF', 'HB', 'KB', 'EK', 'BRF', 'IF']);
export type LegalForm = z.infer<typeof legalFormSchema>;

/** Bolagsformernas namn, som kunden och byrån ser dem. */
export const LEGAL_FORM_NAMES: Record<LegalForm, string> = {
  AB: 'Aktiebolag',
  EF: 'Enskild firma',
  HB: 'Handelsbolag',
  KB: 'Kommanditbolag',
  EK: 'Ekonomisk förening',
  BRF: 'Bostadsrättsförening',
  IF: 'Ideell förening',
};

/** Hur ofta tjänsten debiteras: per månad, per år eller en gång. */
export const billingSchema = z.enum(['monthly', 'yearly', 'once']);
export type Billing = z.infer<typeof billingSchema>;

/**
 * Namnet på en mängd som priset beror på, till exempel transactionsPerMonth.
 * Fältet är fritt så att motorn kan användas av andra branscher senare.
 */
export const driverSchema = z
  .string()
  .regex(/^[a-z][a-zA-Z0-9]*$/, 'Drivaren skrivs i camelCase, till exempel transactionsPerMonth');
export type Driver = z.infer<typeof driverSchema>;

const fixedPricingSchema = z.object({
  type: z.literal('fixed'),
  price: oreSchema,
});

const perUnitPricingSchema = z.object({
  type: z.literal('perUnit'),
  /** Enheten som den visas i offerten, till exempel "anställd". */
  unit: z.string().trim().min(1),
  price: oreSchema,
});

const tierSchema = z.object({
  /** Övre gräns för steget, inklusive. */
  upTo: z.int().positive(),
  price: oreSchema,
});

const tieredPricingSchema = z.object({
  type: z.literal('tiered'),
  driver: driverSchema,
  tiers: z
    .array(tierSchema)
    .min(1)
    .refine((tiers) => tiers.every((tier, i) => i === 0 || tier.upTo > (tiers[i - 1]?.upTo ?? 0)), {
      message: 'Trappstegen måste ha stigande övre gränser',
    }),
});

const byLegalFormPricingSchema = z.object({
  type: z.literal('byLegalForm'),
  prices: z
    .partialRecord(legalFormSchema, oreSchema)
    .refine((prices) => Object.keys(prices).length > 0, {
      message: 'Ange pris för minst en bolagsform',
    }),
});

const timeNormSchema = z.union([
  z.object({
    driver: driverSchema,
    minutesPerUnit: z.number().positive(),
  }),
  z.object({
    fixedMinutes: z.int().positive(),
  }),
]);

/**
 * Hur en rad med timpris visas i offerten:
 * hoursAndRate "5,5–7 tim × 750 kr", amountOnly bara beloppet,
 * monthlyCap ett tak per månad där faktisk tid debiteras.
 */
export const hourlyDisplaySchema = z.enum(['hoursAndRate', 'amountOnly', 'monthlyCap']);
export type HourlyDisplay = z.infer<typeof hourlyDisplaySchema>;

const hourlyPricingSchema = z.object({
  type: z.literal('hourly'),
  /** Timpris i öre. */
  rate: oreSchema,
  timeNorms: z.array(timeNormSchema).min(1),
  minHours: z.number().nonnegative(),
  roundToHours: z.number().positive(),
  display: hourlyDisplaySchema,
});

/** De fem prissättningssätten. */
export const pricingSchema = z.discriminatedUnion('type', [
  fixedPricingSchema,
  perUnitPricingSchema,
  tieredPricingSchema,
  byLegalFormPricingSchema,
  hourlyPricingSchema,
]);
export type Pricing = z.infer<typeof pricingSchema>;
export type PricingType = Pricing['type'];

export const serviceIdSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/,
    'Tjänstens id skrivs med små bokstäver, siffror och bindestreck',
  );
export type ServiceId = z.infer<typeof serviceIdSchema>;

export const serviceSchema = z.object({
  id: serviceIdSchema,
  label: z.string().trim().min(1),
  billing: billingSchema,
  pricing: pricingSchema,
  /** Vad som ingår. Visas under raden i offerten. */
  includes: z.array(z.string().trim().min(1)).optional(),
  /** Bolagsformer som tjänsten gäller. Saknas fältet gäller den alla. */
  appliesTo: z.array(legalFormSchema).min(1).optional(),
  /**
   * Bolagsformer där tjänsten alltid ska med, till exempel bokslutet. Koden
   * lägger till den om AI inte har valt den (ADR 0101). Priset får inte bero
   * på en mängd, eftersom ingen mängd finns när koden lägger till tjänsten.
   */
  requiredFor: z.array(legalFormSchema).min(1).optional(),
});
export type Service = z.infer<typeof serviceSchema>;

/** Om priset går att räkna utan mängder, så att koden kan lägga till tjänsten själv. */
export function pricedWithoutQuantities(pricing: Pricing): boolean {
  switch (pricing.type) {
    case 'fixed':
    case 'byLegalForm':
      return true;
    case 'hourly':
      return pricing.timeNorms.every((norm) => !('driver' in norm));
    default:
      return false;
  }
}

/** Hur många procent AI får justera priset nedåt och uppåt. */
const aiAdjustmentPercentSchema = z.object({
  min: z.int().min(-100).max(0),
  max: z.int().min(0).max(100),
});

export const serviceCatalogSchema = z
  .object({
    currency: z.literal('SEK'),
    /** Momssats som andel, 0.25 för 25 %. */
    vatRate: z.number().min(0).max(1),
    services: z.array(serviceSchema),
    aiAdjustmentPercent: aiAdjustmentPercentSchema,
    /** Över den här månadssumman går förfrågan till granskning. */
    maxAutoQuoteMonthly: oreSchema,
    /** Flaggor från AI som alltid skickar förfrågan till granskning. */
    reviewFlags: z.array(z.string().trim().min(1)),
    /** Raderna avrundas till närmaste multipel av det här beloppet. 500 är 5 kr. */
    roundTo: z.int().positive(),
    /** Hur många dagar offerten gäller. */
    validDays: z.int().positive(),
    /**
     * Visa alltid ett exakt pris, räknat på AI:s bästa uppskattning, i stället
     * för ett intervall när volymen är uppskattad. Saknas fältet visas intervall.
     */
    alwaysExactPrice: z.boolean().optional(),
    terms: z.array(z.string().trim().min(1)),
  })
  .superRefine((catalog, ctx) => {
    const seen = new Set<string>();
    catalog.services.forEach((service, i) => {
      if (seen.has(service.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['services', i, 'id'],
          message: `Tjänsten ${service.id} finns redan i katalogen`,
        });
      }
      seen.add(service.id);
      for (const form of service.requiredFor ?? []) {
        const fel = requiredForIssue(service, form);
        if (fel) {
          ctx.addIssue({ code: 'custom', path: ['services', i, 'requiredFor'], message: fel });
        }
      }
    });
  });

/** Varför tjänsten inte kan vara obligatorisk för bolagsformen, eller undefined. */
function requiredForIssue(service: Service, form: LegalForm): string | undefined {
  if (!pricedWithoutQuantities(service.pricing)) {
    return `Tjänsten ${service.id} kan inte alltid tas med, eftersom priset beror på en mängd`;
  }
  if (service.appliesTo && !service.appliesTo.includes(form)) {
    return `Tjänsten ${service.id} gäller inte ${form} och kan därför inte alltid tas med för den`;
  }
  if (service.pricing.type === 'byLegalForm' && service.pricing.prices[form] === undefined) {
    return `Tjänsten ${service.id} saknar pris för ${form} och kan därför inte alltid tas med för den`;
  }
  return undefined;
}
export type ServiceCatalog = z.infer<typeof serviceCatalogSchema>;
